
import Checked from '@uikit/icons/Checked';

import type { StepsToJoinCampaignStep } from './StepsToJoinCampaign';

export const JOIN_CAMPAIGN_STEP_TYPE = {
  REGISTER_MAGIC_STORE_ACCOUNT: 'REGISTER_MAGIC_STORE_ACCOUNT',
  ADD_EVM_WALLET: 'ADD_EVM_WALLET',
  USERNAME_AND_EMAIL: 'USERNAME_AND_EMAIL',
  SIGNUP_AND_VERIFY_EMAIL: 'SIGNUP_AND_VERIFY_EMAIL',
  LINK_TON_WALLET: 'LINK_TON_WALLET',
  LINK_SOLANA_WALLET: 'LINK_SOLANA_WALLET',
  VERIFY_HUMANITY: 'VERIFY_HUMANITY',
  VERIFY_HUMANITY_LIVENESS: 'VERIFY_HUMANITY_LIVENESS',
  VISIT_CAMPAIGN_PAGE: 'VISIT_CAMPAIGN_PAGE',
  MONITOR_LEADERBOARD: 'MONITOR_LEADERBOARD',
  STORE_TRADE_REWARD: 'STORE_TRADE_REWARD',
  LINK_TELEGRAM: 'LINK_TELEGRAM',
  JOIN_TURTLE_CLUB: 'JOIN_TURTLE_CLUB',
  START_EARNING_WITH_TURTLE_CLUB: 'START_EARNING_WITH_TURTLE_CLUB',
  REGISTER_ON_GOODCRYPTOX: 'REGISTER_ON_GOODCRYPTOX',
  CONNECT_YOUR_GATE_IO: 'CONNECT_YOUR_GATE_IO',
  TRADE_FOXSY_USDT: 'TRADE_FOXSY_USDT',
  CONNECT_WITH_API_KEY_GOODCRYPTOX: 'CONNECT_WITH_API_KEY_GOODCRYPTOX',
  TRADE_SQR_USDT: 'TRADE_SQR_USDT',
  BYBIT_REGISTRATION: 'BYBIT_REGISTRATION',
} as const;

type JoinCampaignStepName = keyof typeof JOIN_CAMPAIGN_STEP_TYPE;

export type JoinCampaignStepType = (typeof JOIN_CAMPAIGN_STEP_TYPE)[JoinCampaignStepName];

const genericStepsToJoinCampaign: Record<JoinCampaignStepType | string, StepsToJoinCampaignStep> = {
  [JOIN_CAMPAIGN_STEP_TYPE.REGISTER_MAGIC_STORE_ACCOUNT]: {
    id: 'register',
    title: 'Register for a Magic Store Account',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.ADD_EVM_WALLET]: {
    id: 'evm-wallet',
    title: 'Add an EVM Wallet to your MagicID',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.USERNAME_AND_EMAIL]: {
    id: 'username-and-email',
    title: 'Create a username and verify your email',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.SIGNUP_AND_VERIFY_EMAIL]: {
    id: 'signup-and-verify-email',
    title: 'Sign up on Magic Store & verify your email',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.LINK_TON_WALLET]: {
    id: 'ton-wallet',
    title: 'Link your TON Wallet to your MagicID',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.LINK_SOLANA_WALLET]: {
    id: 'solana-wallet',
    title: 'Connect Solana Wallet to your MagicID',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.VERIFY_HUMANITY]: {
    id: 'complete-next-tasks',
    title: 'Complete one of the following',
    content: (
      <ul className="flex flex-col gap-2">
        {[
          'Pass Proof-of-Humanity verification',
          'Connect your Gitcoin Passport to your MagicID',
        ].map((item, i) => {
          return (
            <li key={i} className="flex items-center gap-3 max-md:text-sm">
              <Checked className="icon md:icon-[1.8125rem]" />
              {item}
            </li>
          );
        })}
      </ul>
    ),
  },
  [JOIN_CAMPAIGN_STEP_TYPE.VISIT_CAMPAIGN_PAGE]: {
    id: 'visit-campaign-page',
    title: 'Visit the Campaign Page to complete tasks',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.MONITOR_LEADERBOARD]: {
    id: 'leaderboard',
    title: (
      <>
        Monitor your standing on the Leaderboard to track your progress.
        <br />
        Rewards will be distributed based on your campaign XP proportionally
      </>
    ),
  },
  [JOIN_CAMPAIGN_STEP_TYPE.VERIFY_HUMANITY_LIVENESS]: {
    id: 'humanity-liveness',
    title: 'Complete Proof-of-Humanity verification',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.LINK_TELEGRAM]: {
    id: 'link-telegram',
    title: 'Link Telegram to your MagicID',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.JOIN_TURTLE_CLUB]: {
    id: 'join-turtle-club',
    title: 'Confirm Your Membership',
    content: 'Sign the message to confirm your free Turtle Club membership.',
    className: 'max-lg:flex-col max-lg:[&>div:last-child]:self-end',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.START_EARNING_WITH_TURTLE_CLUB]: {
    id: 'start-earning-with-turtle-club',
    title: 'Start Earning with Turtle Club',
    content: 'Begin exploring your earning opportunities today!',
    className: 'max-lg:flex-col max-lg:[&>div:last-child]:self-end',
  },
  [JOIN_CAMPAIGN_STEP_TYPE.REGISTER_ON_GOODCRYPTOX]: {
    id: 'register-on-good-cryptoX',
    title: 'Register on GoodCryptoX with the same email',
    href: 'https://click.goodcrypto.app/b9EC/ie88jiew?ref=wXXJzw',
    buttonLabel: 'Register Now',
  },
};

export const getStepsToJoinCampaign = (
  steps: JoinCampaignStepType[],
): StepsToJoinCampaignStep[] => {
  return steps.map((type) => {
    return {
      ...genericStepsToJoinCampaign[type],
      type,
    };
  });
};
